import * as React from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { useMemo } from "react";

import { useAppSelector } from "../../redux/store.ts";
import { addOptionDialogFactory, isAddNewProductTarget } from "./common.tsx";
import {
  api,
  ProductCreate,
  CreateProductApiProductsPostApiArg,
  CreateProductApiProductsPostApiResponse,
  UpdateProductApiProductsProductIdPutApiArg,
} from "../../api";
import { DropdownItem } from "../../types/shared.ts";

type ProductEditFormProps = {
  formContent: ProductCreate;
  setFormContent: React.Dispatch<React.SetStateAction<ProductCreate>>;
};

const ProductEditForm = ({
  formContent,
  setFormContent,
}: ProductEditFormProps) => {
  // Load contract templates from API
  const { data: contractTemplates } =
    api.endpoints.listContractTemplatesApiContractTemplatesGet.useQuery({
      limit: 10000,
      orderBy: "name",
    });

  // Transform contract templates to dropdown options
  const contractTemplateOptions = useMemo<DropdownItem<string>[]>(() => {
    return (
      contractTemplates?.items?.map((item) => ({
        label: item.name,
        value: item.id,
      })) ?? []
    );
  }, [contractTemplates]);

  return (
    <>
      <div className="field">
        <label>
          Nazwa <span style={{ color: "red" }}> *</span>
        </label>
        <InputText
          value={formContent.name}
          onChange={(e) =>
            setFormContent((prev) => ({
              ...prev,
              name: e.target.value,
            }))
          }
          className="w-full"
          placeholder="Wprowadź nazwę produktu"
        />
      </div>

      <div className="field">
        <label>Szablon umowy sprzedaży</label>
        <Dropdown
          value={formContent.sales_contract_template_id}
          options={contractTemplateOptions}
          onChange={(e) =>
            setFormContent((prev) => ({
              ...prev,
              sales_contract_template_id: e.value as string | null,
            }))
          }
          optionLabel="label"
          optionValue="value"
          placeholder="Wybierz szablon"
          className="w-full"
          showClear
        />
      </div>

      <div className="field">
        <label>Szablon umowy zakupu</label>
        <Dropdown
          value={formContent.purchase_contract_template_id}
          options={contractTemplateOptions}
          onChange={(e) =>
            setFormContent((prev) => ({
              ...prev,
              purchase_contract_template_id: e.value as string | null,
            }))
          }
          optionLabel="label"
          optionValue="value"
          placeholder="Wybierz szablon"
          className="w-full"
          showClear
        />
      </div>
    </>
  );
};

const emptyProduct = {
  name: "",
  sales_contract_template_id: null,
  purchase_contract_template_id: null,
} as const satisfies ProductCreate;

const ProductAddOptionDialog = addOptionDialogFactory<
  ProductCreate,
  | CreateProductApiProductsPostApiArg
  | UpdateProductApiProductsProductIdPutApiArg,
  CreateProductApiProductsPostApiResponse
>({
  defaultValues: emptyProduct,
  FormComponent: ProductEditForm,
  useMutation: () => {
    const [triggerCreate, createState] =
      api.endpoints.createProductApiProductsPost.useMutation();
    const [triggerUpdate, updateState] =
      api.endpoints.updateProductApiProductsProductIdPut.useMutation();

    // Choose the correct mutation based on presence of id
    const trigger = (
      args:
        | CreateProductApiProductsPostApiArg
        | UpdateProductApiProductsProductIdPutApiArg,
    ) => {
      if ("productId" in args && "productUpdate" in args) {
        return triggerUpdate(args);
      } else {
        return triggerCreate(args);
      }
    };

    return [
      trigger,
      {
        isLoading: createState.isLoading || updateState.isLoading,
        reset: () => {
          createState.reset?.();
          updateState.reset?.();
        },
        originalArgs: undefined,
      },
    ] as const;
  },
  createMutationArg: (formContent) => {
    if ("id" in formContent && formContent.id) {
      // Update
      return {
        productId: Number(formContent.id),
        productUpdate: {
          ...formContent,
          sales_contract_template_id:
            formContent.sales_contract_template_id ?? null,
          purchase_contract_template_id:
            formContent.purchase_contract_template_id ?? null,
        },
      };
    } else {
      // Create
      return {
        productCreate: {
          ...formContent,
          sales_contract_template_id:
            formContent.sales_contract_template_id ?? null,
          purchase_contract_template_id:
            formContent.purchase_contract_template_id ?? null,
        },
      };
    }
  },
  validate: (formContent) => !!formContent.name,
  headerText: "Edytuj towar",
});

export const ProductAddOption: React.FC = () => {
  const { target } = useAppSelector((state) => state.addNew);
  if (!isAddNewProductTarget(target)) {
    return null;
  }
  return (
    <ProductAddOptionDialog
      rowId={target.id}
      field={target.field}
      initialValues={target.initialValues}
    />
  );
};
